import useI18n from '@hooks/i18n/useI18n';
import { APPLE_PAY_SUPPORTED_NETWORKS } from '@monofront/business/constants';
import logger from '@utils/logger';
import { useTranslations } from 'next-intl';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import { IStartApplePaySessionResult } from '@monofront/types';
import { START_APPLE_PAY_SESSION } from '@monofront/business/queries';
import { useMutation } from '@apollo/client';

const useApplePay = () => {
  const { countryCode, currency } = useI18n();
  const tGlobal = useTranslations('Global');
  const { handlePaymentError } = useHandlePaymentError();
  const [startApplePaySession] = useMutation<IStartApplePaySessionResult>(
    START_APPLE_PAY_SESSION,
  );

  const requestPayload = (
    totalPrice: number,
  ): ApplePayJS.ApplePayPaymentRequest => {
    return {
      countryCode: countryCode,
      currencyCode: currency,
      supportedNetworks: APPLE_PAY_SUPPORTED_NETWORKS,
      merchantCapabilities: ['supports3DS'],
      total: {
        amount: totalPrice ? (totalPrice / 100).toString() : '0.00',
        label: tGlobal('companyName'),
      },
    };
  };

  const validateApplePayMerchant = async (
    paymentSession: ApplePaySession,
    validationUrl: string,
  ) => {
    try {
      const { data, errors } = await startApplePaySession({
        variables: {
          validationUrl,
          clientOrigin: 'WEB',
        },
      });

      if (errors) {
        logger.error('Start session failed with errors:', errors);
        handlePaymentError({
          code: 'CREATE_NEW_APPLE_PAY_REQUEST_FAILED',
          message: 'Merchant validation failed',
          error: errors,
        });

        return;
      }

      const session = data?.startApplePaySession?.session;

      if (!session) {
        logger.error('Start session failed without session data');
        handlePaymentError({
          code: 'CREATE_NEW_APPLE_PAY_REQUEST_FAILED',
          message: 'Merchant validation failed',
          error: new Error('No session data received'),
        });

        return;
      }

      paymentSession.completeMerchantValidation(session);
    } catch (error) {
      logger.error('Merchant validation failed:', error);
      handlePaymentError({
        code: 'CREATE_NEW_APPLE_PAY_REQUEST_FAILED',
        message: 'Merchant validation failed',
        error,
      });
    }
  };

  return {
    requestPayload,
    validateApplePayMerchant,
  };
};

export default useApplePay;
